import { InfoCircleOutlined } from "@ant-design/icons"
import { BillingPeriodEnum, SubscriptionTypeEnum } from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { SubscriptionRenewalGenerationTypeEnum } from "@finway-group/shared/lib/models/expense/subscriptionRenewalGenerationType.enum"
import { Col, DatePicker, Form, Row, Select } from "antd"
import moment from "moment"
import React, { type ChangeEvent } from "react"
import { useTranslation } from "react-i18next"
import { useDebouncedCallback } from "use-debounce/lib"
import { FormInputElem, FormTextArea } from "~Components/form/formInputAndTextarea"
import TotalAmountForm from "~Components/forms/expenseForm/commonInputFields/totalAmount.form"
import { VendorSelectField } from "~Components/forms/expenseForm/commonInputFields/VendorSelectField"
import { useExpenseFormContext } from "~Components/modals/expenseCreateForm.context"
import { useEmployees } from "~Shared/hooks/employee.hooks"
import { CompanyService } from "~Shared/services"
import { performanceDateHardLimitDisabled } from "~Shared/utils/date.utils"
import { capitalizeFirstLetter } from "~Shared/utils/helper.utils"
import { getPopupAnchor } from "~Shared/utils/popup.utils"
import { ExpenseRules } from "../rules"
import { CurrencySelect } from "./commonInputFields/CurrencySelect"
import { HoverableInfo } from "./reimbursementForms/auxilliary/HoverableInfo"

export const SubscriptionExpenseForm = React.memo(() => {
    const [{ expense, expenseForm: formInstance, updateExpense, isLinked, setIsLinked, type, isNew, prefilledVendorData, setPrefilledVendorData, isBuildingApprovalProcesses }] =
        useExpenseFormContext()

    const { t } = useTranslation()
    const employees = useEmployees()
    const { name, link, datePurchased, dateCancellationDue, description } = ExpenseRules({ expense, employees, formInstance })
    const isCardSubscription = expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD

    const debounceUpdate = useDebouncedCallback((updateData) => {
        updateExpense(updateData)
    }, 600)

    return (
        <div>
            <Row gutter={[16, 16]} align="middle">
                <FormInputElem
                    elem={{
                        name: "name",
                        key: "name",
                        label: t("input:request.name"),
                        placeholder: t("placeholder:request.subscription_name"),
                        initialValue: expense.name,
                        rules: name,
                        maxLength: 100,
                        isRequired: true,
                        isAutoComplete: false,
                        onChange: (e: ChangeEvent<HTMLInputElement>) => debounceUpdate.callback({ name: e.target.value }),
                    }}
                    span={12}
                />
                <FormInputElem
                    elem={{
                        name: "link",
                        key: "link",
                        label: t("input:request.link"),
                        placeholder: t("placeholder:request.link"),
                        initialValue: expense.link,
                        rules: link,
                        onChange: (e: ChangeEvent<HTMLInputElement>) => debounceUpdate.callback({ link: e.target.value }),
                    }}
                    span={12}
                />
            </Row>
            <Row gutter={[16, 16]} align="middle">
                <FormTextArea
                    elem={{
                        rules: description,
                        name: "description",
                        label: t("input:request.description"),
                        placeholder: t("placeholder:request.description"),
                        key: "description",
                        initialValue: expense.description,
                        className: "leading-normal",
                        autoSize: { minRows: 1, maxRows: 3 },
                        isRequired: CompanyService.doesCompanyEnforceGobdCompliantInvoice(),
                        onChange: (e: ChangeEvent<HTMLTextAreaElement>) => debounceUpdate.callback({ description: e.target.value }),
                    }}
                    span={24}
                    sm={24}
                    md={24}
                />
            </Row>
            <TotalAmountForm
                expense={expense}
                expenseForm={formInstance}
                updateExpense={updateExpense}
                isLinked={isLinked}
                setIsLinked={setIsLinked}
                type={type}
                isNew={isNew}
                isLoading={isBuildingApprovalProcesses}
            />
            <Row gutter={[16, 16]} align="middle">
                <Col span={12} sm="24" md="12">
                    <Form.Item
                        label={
                            <>
                                {t("input:request.performance_date")}
                                <HoverableInfo message={t("tooltips:subscriptions.date_started_info")} type="info" iconOverride={<InfoCircleOutlined className="ml-6" />} />
                            </>
                        }
                        key="datePurchased"
                        name="datePurchased"
                        rules={datePurchased}
                        required
                    >
                        <DatePicker
                            disabledDate={performanceDateHardLimitDisabled}
                            name="datePurchased"
                            style={{ width: "100%" }}
                            format={moment.localeData().longDateFormat("L")}
                            placeholder={t("placeholder:request.date")}
                            getPopupContainer={getPopupAnchor()}
                            onChange={(v) => {
                                updateExpense({ datePurchased: v as any })
                                formInstance.validateFields(["dateCancellationDue"])
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col span={12} sm="24" md="12">
                    <Form.Item
                        label={capitalizeFirstLetter(t("input:request.date_cancellation_due"))}
                        key="dateCancellationDue"
                        name="dateCancellationDue"
                        rules={dateCancellationDue}
                        required
                    >
                        <DatePicker
                            name="dateCancellationDue"
                            style={{ width: "100%" }}
                            format={moment.localeData().longDateFormat("L")}
                            placeholder={t("placeholder:request.date")}
                            getPopupContainer={getPopupAnchor()}
                            onChange={(v) => {
                                updateExpense({ dateCancellationDue: v as any })
                                formInstance.validateFields(["datePurchased"])
                            }}
                        />
                    </Form.Item>
                </Col>

                <Col span={12} sm="24">
                    <Form.Item label={t("input:request.billing_period")} key="billingPeriod" name="billingPeriod" initialValue={BillingPeriodEnum.MONTHLY}>
                        <Select getPopupContainer={getPopupAnchor()} onChange={(v: BillingPeriodEnum) => updateExpense({ billingPeriod: v })}>
                            <Select.Option value={BillingPeriodEnum.MONTHLY}>{t("input:request.monthly")}</Select.Option>
                            <Select.Option value={BillingPeriodEnum.QUARTERLY}>{t("input:request.quarterly")}</Select.Option>
                            <Select.Option value={BillingPeriodEnum.YEARLY}>{t("input:request.yearly")}</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12} sm="24">
                    <CurrencySelect initialValue={expense.currency} onCurrencyChange={updateExpense} />
                </Col>
                {expense.subscriptionType === SubscriptionTypeEnum.INITIAL_REQUEST && isCardSubscription && (
                    <Col span={12} sm="24">
                        <Form.Item
                            label={t("input:request.renewal_generation.title")}
                            key="renewalGenerationType"
                            name="renewalGenerationType"
                            initialValue={SubscriptionRenewalGenerationTypeEnum.BY_DATE}
                        >
                            <Select getPopupContainer={getPopupAnchor()} onChange={(v: SubscriptionRenewalGenerationTypeEnum) => updateExpense({ renewalGenerationType: v })}>
                                <Select.Option value={SubscriptionRenewalGenerationTypeEnum.BY_DATE}>{t("input:request.renewal_generation.by_date")}</Select.Option>
                                <Select.Option value={SubscriptionRenewalGenerationTypeEnum.BY_TRANSACTION}>{t("input:request.renewal_generation.by_transaction")}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                )}
                {expense.subscriptionType === SubscriptionTypeEnum.RENEWAL_REQUEST && (
                    <Col span={12}>
                        <Form.Item label={t("label:performance_period")} name="performancePeriod" className="text-opacity-50">
                            <DatePicker.RangePicker
                                disabledDate={performanceDateHardLimitDisabled}
                                name="performancePeriod"
                                style={{ width: "100%" }}
                                allowEmpty={[true, true]}
                                format={moment.localeData().longDateFormat("L")}
                                placeholder={[t("placeholder:start_date"), t("placeholder:end_date")]}
                                getPopupContainer={getPopupAnchor()}
                                onChange={(values) => {
                                    updateExpense({ performancePeriod: values })
                                }}
                            />
                        </Form.Item>
                    </Col>
                )}
                <Col span={24}>
                    <VendorSelectField
                        expense={expense}
                        updateExpense={updateExpense}
                        expenseForm={formInstance}
                        prefilledVendorData={prefilledVendorData}
                        setPrefilledVendorData={setPrefilledVendorData}
                    />
                </Col>
            </Row>
        </div>
    )
})
